import { create } from 'zustand'

/**
 * Login-time role flags. The user's actual permissions come from `/permissions/me`
 * (see `usePermission` in hooks/usePermission); these flags only bridge the moment before it loads.
 */
interface PermissionState {
  isDeveloper: boolean
  isSuperUser: boolean

  setIsDeveloper: (isDeveloper: boolean) => void
  setIsSuperUser: (isSuperUser: boolean) => void
  clear: () => void
}

export const usePermissionStore = create<PermissionState>((set) => ({
  isDeveloper: false,
  isSuperUser: false,

  setIsDeveloper: (isDeveloper) => set({ isDeveloper }),
  setIsSuperUser: (isSuperUser) => set({ isSuperUser }),
  clear: () => set({ isDeveloper: false, isSuperUser: false }),
}))

/**
 * Canonical permission keys — must stay in sync with the backend PermissionKey enum
 * (labels, descriptions and grouping come from GET /permissions/catalog).
 * Format: resource:action  (e.g. record_group:create)
 */
export const PERMISSION_KEYS = {
  // ── Workspace ────────────────────────────────────────────────────────────
  WORKSPACE_VIEW:   'workspace:view',
  WORKSPACE_UPDATE: 'workspace:update',
  WORKSPACE_INVITE: 'workspace:invite',

  // ── Record Group ─────────────────────────────────────────────────────────
  RECORD_GROUP_VIEW:               'record_group:view',
  RECORD_GROUP_CREATE:             'record_group:create',
  RECORD_GROUP_UPDATE:             'record_group:update',
  RECORD_GROUP_DELETE:             'record_group:delete',
  RECORD_GROUP_MANAGE_PERMISSIONS: 'record_group:manage_permissions',

  // ── Form ─────────────────────────────────────────────────────────────────
  FORM_VIEW:   'form:view',
  FORM_CREATE: 'form:create',
  FORM_UPDATE: 'form:update',
  FORM_DELETE: 'form:delete',

  // ── Form Public Link ─────────────────────────────────────────────────────
  FORM_PUBLIC_LINK_VIEW:   'form_public_link:view',
  /** Create, edit, disable and delete public links (merged from create/update/delete). */
  FORM_PUBLIC_LINK_MANAGE: 'form_public_link:manage',

  // ── Form Template ────────────────────────────────────────────────────────
  FORM_TEMPLATE_VIEW:   'form_template:view',
  FORM_TEMPLATE_CREATE: 'form_template:create',
  FORM_TEMPLATE_UPDATE: 'form_template:update',
  FORM_TEMPLATE_DELETE: 'form_template:delete',

  // ── Record ───────────────────────────────────────────────────────────────
  RECORD_VIEW:   'record:view',
  RECORD_CREATE: 'record:create',
  RECORD_UPDATE: 'record:update',
  RECORD_DELETE: 'record:delete',
  RECORD_EXPORT: 'record:export',
  /** Add/remove record tags (restricted tags also need TAG_USE). */
  RECORD_TAG_ASSIGN: 'record:assign_tags',

  // ── Tags ─────────────────────────────────────────────────────────────────
  /** Create, edit, restrict and archive a group's tags and categories; set the default tag. */
  TAG_MANAGE: 'tag:manage',
  /** Put RESTRICTED tags on things (open tags need only the target's assign key). */
  TAG_USE:    'tag:use',

  // ── Automation ───────────────────────────────────────────────────────────
  AUTOMATION_VIEW:   'automation:view',
  AUTOMATION_CREATE: 'automation:create',
  AUTOMATION_UPDATE: 'automation:update',
  AUTOMATION_DELETE: 'automation:delete',
  AUTOMATION_TOGGLE: 'automation:toggle',
  AUTOMATION_RUN:    'automation:run',

  // ── Activity ─────────────────────────────────────────────────────────────
  ACTIVITY_VIEW: 'record_activity:view',
  ACTIVITY_TAG_ASSIGN: 'record_activity:assign_tags',
  FOLLOWUP_TAG_ASSIGN: 'record_activity:assign_followup_tags',
  ACTIVITY_ADD:  'record_activity:add',

  // ── User ─────────────────────────────────────────────────────────────────
  USER_VIEW:               'user:view',
  USER_CREATE:             'user:create',
  USER_UPDATE:             'user:update',
  USER_TOGGLE:             'user:toggle',
  USER_DELETE:             'user:delete',
  USER_MANAGE_PERMISSIONS: 'user:manage_permissions',
  USER_MANAGE_ROLES:       'user:manage_roles',

  // ── Role ─────────────────────────────────────────────────────────────────
  ROLE_VIEW:               'role:view',
  ROLE_CREATE:             'role:create',
  /** Includes enabling/disabling roles (role:toggle was merged into it). */
  ROLE_UPDATE:             'role:update',
  ROLE_DELETE:             'role:delete',
  ROLE_MANAGE_PERMISSIONS: 'role:manage_permissions',
  ROLE_MANAGE_USERS:       'role:manage_users',

  // ── Subscription ─────────────────────────────────────────────────────────
  SUBSCRIPTION_VIEW:             'subscription:view',
  SUBSCRIPTION_CREATE:           'subscription:create',
  SUBSCRIPTION_SUBSCRIBE:        'subscription:subscribe',
  SUBSCRIPTION_CHANGE_PLAN:      'subscription:change_plan',
  SUBSCRIPTION_EDIT_ENTITLEMENTS:'subscription:edit_entitlements',
  SUBSCRIPTION_CANCEL:           'subscription:cancel',

  // ── Payment ──────────────────────────────────────────────────────────────
  PAYMENT_ORDER_CREATE:  'payment_order:create',
  PAYMENT_HISTORY_VIEW:  'payment_history:view',

  // ── Notification ─────────────────────────────────────────────────────────
  NOTIFICATION_VIEW: 'notification:view',

  // ── Dashboard ────────────────────────────────────────────────────────────
  DASHBOARD_CREATE: 'dashboard:create',
  DASHBOARD_VIEW: 'dashboard:view',
  DASHBOARD_UPDATE: 'dashboard:update',
  DASHBOARD_DELETE: 'dashboard:delete',
  DASHBOARD_WIDGET_VIEW: 'dashboard_widget:view',

  // ── Task ──────────────────────────────────────────────────────────────────
  TASK_VIEW:   'task:view',
  TASK_CREATE: 'task:create',
  TASK_UPDATE: 'task:update',
  TASK_DELETE: 'task:delete',

  // ── Calling ──────────────────────────────────────────────────────────────
  CALL_MAKE:           'call:make',
  /** Every call in the workspace (otherwise own calls and calls on viewable records). */
  CALL_VIEW_ALL:       'call:view_all',
  /** Telephony provider, dispositions and agent phones; includes CALL_VIEW_ALL. */
  CALL_CONFIGURE:      'call:configure',
  CALL_PLAY_RECORDING: 'call:play_recording',

  // ── Mailboxes ────────────────────────────────────────────────────────────
  /** Search workspace mailboxes and send automation emails from them. */
  MAILBOX_USE_WORKSPACE: 'mailbox:use_workspace',
  /** Connect and remove shared mailboxes; includes MAILBOX_USE_WORKSPACE. */
  MAILBOX_MANAGE:        'mailbox:manage',

  // ── Developer ────────────────────────────────────────────────────────────
  DEVELOPER_DASHBOARD_VIEW: 'developer_dashboard:view',
} as const

export type PermissionKeyValue = (typeof PERMISSION_KEYS)[keyof typeof PERMISSION_KEYS]
